/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	ClaimConstants
} from '@claims/constants/claims-constants';
import {
	ClaimsService
} from '@claims/services/claims.service';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';

/* eslint-enable max-len */

@Component({
	selector: 'claim-payment-payee',
	templateUrl: './claim-payment-payee.component.html',
	styleUrls: [
		'./claim-payment-payee.component.scss'
	]
})

/**
 * A component representing a claim payment payee.
 *
 * @export
 * @class ClaimPaymentPayeeComponent
 * @implements {IDynamicComponent<Component, any>}
 * @implements {OnInit}
 */
export class ClaimPaymentPayeeComponent
implements IDynamicComponent<Component, any>, OnInit
{
	/**
	 * Creates an instance of an claim payment payee
	 * component.
	 *
	 * @param {ClaimsService} claimsService
	 * The claims service for this component.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service for this component.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service for this component.
	 * @memberof ClaimPaymentPayeeComponent
	 */
	public constructor(
		public claimsService: ClaimsService,
		public entityInstanceApiService: EntityInstanceApiService,
		public siteLayoutService: SiteLayoutService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<Component, any>}
	 * @memberof ClaimPaymentPayeeComponent
	 */
	@Input() public context: IDynamicComponentContext<Component, any>;

	/**
	 * Gets or sets the claim payment instance.
	 *
	 * @type {IEntityInstance}
	 * @memberof ClaimPaymentPayeeComponent
	 */
	public claimPaymentInstance: IEntityInstance;

	/**
	 * Gets or sets the claim payment id.
	 *
	 * @type {number}
	 * @memberof ClaimPaymentPayeeComponent
	 */
	public claimPaymentId: number;

	/**
	 * Gets or sets the payee name.
	 *
	 * @type {string}
	 * @memberof ClaimPaymentPayeeComponent
	 */
	public payeeName: string;

	/**
	 * Gets or sets the payee address.
	 *
	 * @type {string}
	 * @memberof ClaimPaymentPayeeComponent
	 */
	public payeeAddress: string;

	/**
	 * Gets or sets the value signifying whether or not this component is
	 * loading.
	 *
	 * @type {boolean}
	 * @memberof ClaimPaymentPayeeComponent
	 */
	public loading: boolean = true;

	/**
	 * Initiates the component to get the data object.
	 *
	 * @async
	 * @memberof ClaimPaymentPayeeComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		this.claimPaymentId =
			(<any>this.context.source)
				.entityContext?.source.activeMenuItem
				.currentData.data.id;

		this.entityInstanceApiService.entityInstanceTypeGroup =
			ClaimConstants.claimEntityTypeGroups.claimPayments;

		this.claimPaymentInstance =
			await this.entityInstanceApiService.get(this.claimPaymentId);

		this.payeeName =
			this.claimPaymentInstance.data?.payee?.name;

		this.payeeAddress =
			StringHelper.toAddressString(
				this.claimPaymentInstance.data?.payee?.address?.address,
				this.claimPaymentInstance.data?.payee?.address?.city,
				this.claimPaymentInstance.data?.payee?.address?.state,
				this.claimPaymentInstance.data?.payee?.address?.postalCode,
				this.claimPaymentInstance.data?.payee?.address
					?.principalSubdivision,
				this.claimPaymentInstance.data?.payee?.address?.country);

		this.loading = false;
		(<any>this.context.source).finishedLoading?.emit();
	}
}